// 👇️ ts-nocheck
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Table as AntTable, Avatar, Button, Col, Tag } from "antd";
import { notify } from "@/shared/utils/notify";
import { Edit, GripVertical, List, Trash2 } from "lucide-react";
import { bannersService } from "../../../shared/services";
import { updateBanner } from "../../../redux";
import { DataBanner } from "../../../redux/@types";
import { useBanners, useTableBanners } from "../hooks";
import { PATH_S3_BUCKET } from "../../../shared/utils/constants";
import { LucideIcon } from "../../../shared/components/ui/icons/LucideIcon";
import { TableColumns } from "../../../shared/components/ui/Table/types";

function createDraggableWrapper(rowKeys: string[]) {
  return function DraggableWrapper(props: any) {
    const { children, ...restProps } = props;
    return (
      <SortableContext
        items={rowKeys}
        strategy={verticalListSortingStrategy}
      >
        <tbody {...restProps} style={{ ...restProps.style, pointerEvents: "auto" }}>
          {children}
        </tbody>
      </SortableContext>
    );
  };
}

function DraggableRow(props: any) {
  const rowKey = props["data-row-key"] ?? props.record?.id ?? props.index;
  const id = String(rowKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const { children, ...restProps } = props;
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { zIndex: 999, background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" } : {}),
  };
  return (
    <tr
      ref={setNodeRef}
      {...restProps}
      {...attributes}
      {...listeners}
      style={{
        ...style,
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
    >
      {children}
    </tr>
  );
}

const typeLabels: Record<string, string> = {
  HOME: "Home WEB",
  HOME_MOBILE: "Home APP",
  OFFERS: "Ofertas",
  POPUP: "Popup",
  ACADEMY: "Academia",
  FLASH_SALE: "Ofertas Relámpago",
};

const actionTypeLabels: Record<string, string> = {
  BANNER_PRODUCTS: "Banner de productos",
  CATEGORY: "Categoría",
  PRODUCT: "Producto",
  URL: "URL",
  COURSE: "Curso",
  PROMOTION: "Promoción",
  FIFTY_OFF: "Rollos 50%",
};

export const TableBannersSortable = () => {
  const { showDeleteConfirm, changeBannersForUpdate } = useTableBanners();
  const { setTableProps, changeNavToBannersProducts, updateStatus, isLoading } = useBanners();
  const [data, setData] = useState<DataBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bannersService.find({
        query: {
          $limit: 500,
          $sort: { priority: -1, id: -1 },
        },
      });
      const items = (res as any)?.data ?? [];
      setData(items);
    } catch (err) {
      notify.error("Error al cargar banners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setTableProps?.({ refresh: fetchData });
  }, [setTableProps, fetchData]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => String(item.id) === active.id);
    const newIndex = data.findIndex((item) => String(item.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(data, oldIndex, newIndex);
    setData(reordered);

    try {
      const totalItems = reordered.length;
      await Promise.all(
        reordered.map((item, index) =>
          updateBanner({
            id: item.id,
            priority: totalItems - index,
          })
        )
      );
      notify.success("Orden actualizado correctamente");
    } catch (err: any) {
      notify.error(err?.message || "Error al actualizar orden");
      fetchData();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const columns: TableColumns<DataBanner> = [
    {
      key: "dragHandle",
      dataIndex: "dragHandle",
      title: "",
      width: 48,
      render: () => (
        <span
          title="Arrastra para reordenar"
          style={{
            color: "#999",
            display: "inline-flex",
            padding: "4px",
          }}
        >
          <GripVertical size={20} />
        </span>
      ),
    },
    {
      title: "ID",
      width: 5,
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Vista en móvil",
      width: 7,
      dataIndex: "path_movile",
      key: "path_movile",
      render: (_, item) => (
        <Col style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
          <Avatar
            size={64}
            shape="square"
            src={item.path_movile ? `${PATH_S3_BUCKET}/${item.path_movile}` : null}
            style={{ borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#f7fafc", color: "#718096" }}
          >
            {!item.path_movile && "Sin foto"}
          </Avatar>
        </Col>
      ),
    },
    {
      title: "Vista en escritorio",
      width: 7,
      dataIndex: "path_desktop",
      key: "path_desktop",
      render: (_, item) => (
        <Col style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
          <Avatar
            size={64}
            shape="square"
            src={item.path_desktop ? `${PATH_S3_BUCKET}/${item.path_desktop}` : null}
            style={{ borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#f7fafc", color: "#718096" }}
          >
            {!item.path_desktop && "Sin foto"}
          </Avatar>
        </Col>
      ),
    },
    {
      title: "Tipo",
      width: 10,
      dataIndex: "type",
      key: "type",
      render: (_, item) => typeLabels[item.type] ?? item.type,
    },
    {
      title: "Estado",
      width: 7,
      dataIndex: "status",
      key: "status",
      render: (_, item) => (
        <Col style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
          <Tag
            color={item.status === "active" ? "success" : "error"}
            style={{
              borderRadius: "6px",
              padding: "4px 12px",
              fontSize: "13px",
              fontWeight: 500,
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
            }}
            onClick={() => {
              if (!isLoading) {
                if (item.status === "active") updateStatus("inactive", item.id);
                else if (item.status === "inactive") updateStatus("active", item.id);
              }
            }}
          >
            {item.status === "active" ? "Activo" : "Inactivo"}
          </Tag>
        </Col>
      ),
    },
    {
      title: "Prioridad",
      width: 6,
      dataIndex: "priority",
      key: "priority",
    },
    {
      title: "Tipo de acción",
      width: 10,
      dataIndex: "action_type",
      key: "action_type",
      render: (_, item) => actionTypeLabels[item.action_type] ?? item.action_type,
    },
    {
      title: "Acciones",
      key: "operation",
      fixed: "right",
      width: 10,
      render: (_, item) => (
        <Col style={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center", gap: "8px" }}>
          <Button
            type="text"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => changeBannersForUpdate(item)}
            style={{ borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px" }}
          >
            <LucideIcon>
              <Edit size={18} />
            </LucideIcon>
          </Button>
          {item.action_type === "BANNER_PRODUCTS" && (
            <Button
              type="text"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => changeNavToBannersProducts(item.id)}
              style={{ borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px", color: "#1890ff" }}
            >
              <LucideIcon>
                <List size={18} />
              </LucideIcon>
            </Button>
          )}
          <Button
            type="text"
            danger
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => showDeleteConfirm(item.id, `${item.id}`)}
            style={{ borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px" }}
          >
            <LucideIcon>
              <Trash2 size={18} />
            </LucideIcon>
          </Button>
        </Col>
      ),
    },
  ];

  const bodyWrapper = useMemo(
    () => createDraggableWrapper(data.map((d) => String(d.id))),
    [data]
  );

  const activeItem = activeId ? data.find((d) => String(d.id) === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div style={{ margin: "15px 0", width: "100%", alignSelf: "stretch" }}>
        <p style={{ marginBottom: 8, color: "#666", fontSize: 13 }}>
          Arrastra una fila para cambiar el orden de los banners.
        </p>
        <AntTable<DataBanner>
          className="tableStyleOne modern-pagination"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(record) => String(record.id)}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (t) => `${t} banners`,
            locale: { items_per_page: "por página" },
          }}
          components={{
            body: {
              wrapper: bodyWrapper,
              row: DraggableRow,
            },
          }}
        />
        <DragOverlay>
          {activeItem ? (
            <div
              style={{
                background: "#fff",
                padding: "12px 16px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                cursor: "grabbing",
                border: "1px solid #e8e8e8",
              }}
            >
              Banner #{activeItem.id} - {typeLabels[activeItem.type] ?? activeItem.type}
            </div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  );
};
